<template>
  <div>
    <div class="ft-stat-row">
      <div class="ft-stat-box">
        <div class="ft-stat-icon ft-pending"><i class="fas fa-hourglass-half"></i></div>
        <div>
          <div class="ft-num">{{ counts.pending }}</div>
          <div class="ft-label">Pending</div>
        </div>
      </div>
      <div class="ft-stat-box">
        <div class="ft-stat-icon ft-approved"><i class="fas fa-check"></i></div>
        <div>
          <div class="ft-num">{{ counts.approved }}</div>
          <div class="ft-label">Approved</div>
        </div>
      </div>
      <div class="ft-stat-box">
        <div class="ft-stat-icon ft-rejected"><i class="fas fa-times"></i></div>
        <div>
          <div class="ft-num">{{ counts.rejected }}</div>
          <div class="ft-label">Rejected</div>
        </div>
      </div>
    </div>

    <div class="ft-alert-success" v-if="successMsg"><i class="fas fa-check-circle"></i> {{ successMsg }}</div>

    <div class="ft-card">
      <div class="ft-card-header-row">
        <h3 class="ft-card-title"><i class="fas fa-bullhorn"></i> Seminar Invitations</h3>
      </div>

      <form class="ft-filter-form" @submit.prevent="load(1)">
        <div class="ft-field">
          <label>Status</label>
          <select v-model="filters.status">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div class="ft-field">
          <label>Date From</label>
          <input type="date" v-model="filters.date_from">
        </div>
        <div class="ft-field">
          <label>Date To</label>
          <input type="date" v-model="filters.date_to">
        </div>
        <div class="ft-field" style="min-width:220px;">
          <label>Search</label>
          <input type="text" v-model="filters.search" placeholder="Name, company, email">
        </div>
        <div class="ft-field">
          <button type="submit" class="ft-btn"><i class="fas fa-filter"></i> Filter</button>
        </div>
        <div class="ft-field">
          <button type="button" class="ft-btn ft-btn-outline" @click="resetFilters"><i class="fas fa-undo"></i> Reset</button>
        </div>
      </form>

      <div class="ft-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Requested By</th>
              <th>Preferred Date</th>
              <th>Location</th>
              <th>Participants</th>
              <th>Status</th>
              <th>Admin Notes</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in requests" :key="row.id">
              <td>
                <strong>{{ row.full_name }}</strong><br>
                {{ row.company_name }}<span v-if="row.job_title"> &middot; {{ row.job_title }}</span>
                <span v-if="row.industry"> ({{ row.industry }})</span><br>
                {{ row.email }}<br>{{ row.phone_number }}
              </td>
              <td>{{ row.preferred_date ? formatDate(row.preferred_date) : '-' }}</td>
              <td>{{ locationLabel(row.location_preference) }}</td>
              <td>{{ row.participants || '-' }}</td>
              <td><span class="ft-badge" :class="'ft-badge-'+row.status">{{ row.status }}</span></td>
              <td style="max-width:180px;">{{ row.admin_notes || '-' }}</td>
              <td class="ft-action-cell">
                <button class="ft-btn ft-btn-sm ft-btn-outline" @click="toggleEdit(row.id, $event)">
                  <i class="fas" :class="editingId === row.id ? 'fa-times' : 'fa-pencil-alt'"></i> {{ editingId === row.id ? 'Close' : 'Update' }}
                </button>
                <div v-if="editingId === row.id" class="ft-edit-panel" :style="editPanelStyle">
                  <label class="ft-field-label">Status</label>
                  <select v-model="editForm.status">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <textarea v-model="editForm.admin_notes" rows="2" placeholder="Admin notes"></textarea>
                  <div class="ft-action-cell">
                    <button class="ft-btn ft-btn-sm" @click="saveStatus(row.id)" :disabled="saving"><i class="fas fa-check"></i> Save</button>
                    <button class="ft-btn ft-btn-sm ft-btn-danger" @click="remove(row.id)"><i class="fas fa-trash"></i> Delete</button>
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="!requests.length"><td colspan="7">
              <div class="ft-empty-state"><i class="fas fa-inbox"></i> No seminar invitations found.</div>
            </td></tr>
          </tbody>
        </table>
      </div>

      <div class="ft-pagination">
        <button :disabled="page <= 1" @click="load(page - 1)"><i class="fas fa-chevron-left"></i></button>
        <span class="ft-page-info">Page {{ page }} of {{ lastPage }}</span>
        <button :disabled="page >= lastPage" @click="load(page + 1)"><i class="fas fa-chevron-right"></i></button>
      </div>
    </div>
  </div>
</template>

<script>
import seminar from '@/services/adminSeminar.service';
import Api from '@/helpers/adminApi';
import moment from 'moment';

export default {
  name: 'AdminSeminar',
  data() {
    return {
      requests: [],
      counts: { pending: 0, approved: 0, rejected: 0 },
      filters: { status: '', date_from: '', date_to: '', search: '' },
      page: 1,
      lastPage: 1,
      editingId: null,
      editForm: { status: '', admin_notes: '' },
      saving: false,
      successMsg: '',
      editPanelStyle: {},
    };
  },
  mounted() {
    this.load(1);
  },
  methods: {
    load(page) {
      this.page = page;
      let context = this;
      Api(context, seminar.index({ ...this.filters, page })).onSuccess(function (response) {
        context.requests = response.data.data.data;
        context.lastPage = response.data.data.last_page;
        context.counts = response.data.counts;
      }).call();
    },
    resetFilters() {
      this.filters = { status: '', date_from: '', date_to: '', search: '' };
      this.load(1);
    },
    locationLabel(value) {
      if (value === 'tef_office') return 'At TEF facility';
      if (value === 'client_site') return "At requester's site";
      return '-';
    },
    toggleEdit(id, event) {
      if (this.editingId === id) {
        this.editingId = null;
        return;
      }
      const row = this.requests.find(r => r.id === id);
      this.editForm = { status: row.status, admin_notes: row.admin_notes || '' };
      this.editingId = id;
      this.positionEditPanel(event);
    },
    // Same rationale as AdminFactoryRequest.vue: position:fixed with
    // JS-computed coordinates, since position:absolute inside a table cell
    // doesn't reliably stack above sibling content across browsers.
    positionEditPanel(event) {
      const btn = event.target.closest('button');
      const rect = btn.getBoundingClientRect();
      const panelWidth = 230;
      let left = rect.right - panelWidth;
      if (left < 8) left = 8;
      this.editPanelStyle = { top: (rect.bottom + 8) + 'px', left: left + 'px' };
    },
    saveStatus(id) {
      this.saving = true;
      let context = this;
      Api(context, seminar.updateStatus(id, this.editForm)).onSuccess(function () {
        context.successMsg = 'Seminar request status updated.';
        context.editingId = null;
        context.load(context.page);
        setTimeout(() => { context.successMsg = ''; }, 3000);
      }).onFinish(function () {
        context.saving = false;
      }).call();
    },
    remove(id) {
      if (!confirm('Delete this seminar invitation?')) return;
      let context = this;
      Api(context, seminar.delete(id)).onSuccess(function () {
        context.successMsg = 'Seminar invitation deleted.';
        context.load(context.page);
        setTimeout(() => { context.successMsg = ''; }, 3000);
      }).call();
    },
    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    },
  },
};
</script>
